import { clientOptions } from '../../../core/billing/billing.core.js';

export class BillingOverviewEventAdapter {
  #apiAdapter;
  #overviewComponent;
  #notificationComponent;
  #reloadPage;
  /** Whether the overview has been asked for at least once, so `billing-changed` has a list to refresh. */
  #shown = false;
  /** Number of the newest load; an older load's answer is dropped. */
  #loads = 0;
  /** Whether the clients for the "Add item" form are being fetched, so a second press does not fetch them again. */
  #loadingClients = false;
  /** Changes on each visit so an earlier visit's client lookup cannot open this visit's form. */
  #visit = 0;
  /** Bumped by `disconnect`, so an answer that arrives after it (a logout) is dropped. */
  #session = 0;

  /**
   * @param {() => void} [reloadPage] reloads the app so index.js shows the login screen
   */
  constructor(apiAdapter, overviewComponent, notificationComponent, reloadPage = location.reload.bind(location)) {
    this.#apiAdapter = apiAdapter;
    this.#overviewComponent = overviewComponent;
    this.#notificationComponent = notificationComponent;
    this.#reloadPage = reloadPage;
  }

  connect() {
    document.addEventListener('billing-changed', this.#onChanged);
    document.addEventListener('billing-add-request', this.#onAddRequest);
    document.addEventListener('billing-add-save', this.#onAddSave);
  }

  disconnect() {
    document.removeEventListener('billing-changed', this.#onChanged);
    document.removeEventListener('billing-add-request', this.#onAddRequest);
    document.removeEventListener('billing-add-save', this.#onAddSave);
    this.#loadingClients = false;
    this.#shown = false;
    this.#overviewComponent.loading = false;
    this.#session++;
    this.#visit++;
    // A form left open by a save still running at logout must not greet the next session.
    const form = this.#overviewComponent.addItem;
    form.closeForm();
    form.saving = false;
  }

  /** @returns {boolean} whether the overview is on screen (not replaced by another route) */
  #visible() {
    return this.#overviewComponent.style.display !== 'none';
  }

  /** Shows the overview in the "Due" view, freshly loaded together with the report. */
  async showOverview() {
    this.#visit++;
    this.#loadingClients = false;
    this.#shown = true;
    this.#overviewComponent.dueOnly = true;
    await this.#load();
  }

  /**
   * Reads all active items (the component filters the Due view itself, since a due client's invoice holds all
   * its items), report and settings independently; only the latest request in this login session counts.
   */
  async #load() {
    const load = ++this.#loads;
    const session = this.#session;
    this.#overviewComponent.loading = true;
    try {
      const [items, report, summary] = await Promise.allSettled([
        this.#apiAdapter.getBilling(),
        this.#apiAdapter.getBillingReport(),
        this.#apiAdapter.getBillingSummary(),
      ]);
      if (load !== this.#loads || session !== this.#session) return;
      let failed = false;
      if (items.status === 'fulfilled') {
        this.#overviewComponent.items = items.value;
      } else {
        failed = true;
        this.#handleError(items.reason, 'Could not load the billing overview');
      }
      if (report.status === 'rejected') {
        failed = true;
        this.#overviewComponent.showReportUnavailable();
        this.#handleError(report.reason, 'Could not load the billing report');
      } else {
        this.#overviewComponent.report = report.value;
      }
      if (summary.status === 'rejected') {
        failed = true;
        this.#overviewComponent.showSummaryUnavailable();
        this.#handleError(summary.reason, 'Could not load the billing settings');
      } else {
        this.#overviewComponent.summary = summary.value;
      }
      if (!failed) {
        this.#notificationComponent.clearError();
      }
    } finally {
      if (load === this.#loads && session === this.#session) {
        this.#overviewComponent.loading = false;
      }
    }
  }

  /** Any billing mutation may change the reminders and the recorded results, so all are read again. */
  #onChanged = async () => {
    if (this.#shown && this.#visible()) {
      await this.#load();
    }
  };

  /**
   * Opens the "Add item" form with the clients to choose from; a form already open just gets the cursor back.
   * Clients that arrive after the user left the overview or logged out are dropped, form and messages alike.
   */
  #onAddRequest = async () => {
    const form = this.#overviewComponent.addItem;
    if (form.isOpen) {
      form.focusForm();
      return;
    }
    if (this.#loadingClients) return;
    this.#loadingClients = true;
    const session = this.#session;
    const visit = this.#visit;
    try {
      const clients = await this.#apiAdapter.getAllClients();
      if (session !== this.#session || visit !== this.#visit || !this.#visible()) return;
      if (clients.length === 0) {
        this.#notificationComponent.show('Add a client before adding billing items', 'error');
        return;
      }
      form.open(clientOptions(clients));
    } catch (error) {
      if (session !== this.#session || visit !== this.#visit || !this.#visible()) return;
      this.#handleError(error, 'Could not load the clients');
    } finally {
      if (session === this.#session && visit === this.#visit) {
        this.#loadingClients = false;
      }
    }
  };

  /**
   * Creates the item. A rejected save keeps the form open with the reason shown in it, and also as a message
   * when the user has left the overview meanwhile; a saved one refreshes the page through `billing-changed`.
   * An answer that arrives after a logout is dropped.
   */
  #onAddSave = async (event) => {
    const { slug, request } = event.detail;
    const form = this.#overviewComponent.addItem;
    if (form.saving) return;
    form.saving = true;
    const session = this.#session;
    let saved;
    try {
      saved = await this.#apiAdapter.createBillingItem(slug, request);
    } catch (error) {
      if (session !== this.#session) return;
      this.#handleError(error, 'Could not save billing item', (message) => form.showFormError(message) && this.#visible());
      return;
    } finally {
      if (session === this.#session) form.saving = false;
    }
    if (session !== this.#session) return;
    form.closeForm();
    this.#notificationComponent.show('Billing item added', 'success');
    document.dispatchEvent(new CustomEvent('billing-changed'));
  };

  /**
   * @param {{status?: number, message: string}} error
   * @param {string} action what failed, shown before the server's message
   * @param {(message: string) => boolean} [showInForm] shows the message in the "Add item" form; returns whether the user sees it there
   */
  #handleError(error, action, showInForm) {
    if (error.status === 401) {
      this.#notificationComponent.show('Session expired. Please login again.', 'error');
      setTimeout(() => this.#reloadPage(), 2000);
    } else if (!showInForm?.(`${action}: ${error.message}`)) {
      this.#notificationComponent.show(`${action}: ${error.message}`, 'error');
    }
  }
}
